import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Icon } from '@astral/ui';
import { MAX_PRESET_EMOJIS } from '@astral/shared';
import { useT } from '../providers/I18nProvider.js';
import { EmojiBoard } from './EmojiBoard.js';

export interface EmojiSequenceEditorProps {
  emojis: string[];
  onChange: (next: string[]) => void;
  /** Maximum sequence length (default = MAX_PRESET_EMOJIS). */
  max?: number;
}

const HOLD_MS = 150;
const MOVE_START_PX = 8;

/**
 * Edit an ordered emoji sequence: add (via the inline board / native keyboard),
 * remove, and touch drag-and-drop to reorder. Used by preset editing, the bulk
 * "sequence" mode, and per-sticker multi-emoji editing.
 */
export function EmojiSequenceEditor({ emojis, onChange, max = MAX_PRESET_EMOJIS }: EmojiSequenceEditorProps): JSX.Element {
  const t = useT();
  const [adding, setAdding] = useState(emojis.length === 0);
  const listRef = useRef<HTMLDivElement>(null);

  const emojisRef = useRef(emojis);
  emojisRef.current = emojis;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const overRef = useRef<number | null>(null);
  const session = useRef<{ index: number; startX: number; startY: number; active: boolean } | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishRef = useRef<(commit: boolean) => void>(() => {});

  const full = emojis.length >= max;

  const append = (emoji: string): void => {
    if (emojisRef.current.length >= max) return;
    onChange([...emojisRef.current, emoji]);
  };
  const removeAt = (index: number): void => {
    onChange(emojisRef.current.filter((_, i) => i !== index));
  };

  const hitTest = useCallback((x: number, y: number): number | null => {
    const list = listRef.current;
    if (!list) return null;
    const chips = list.querySelectorAll<HTMLElement>('[data-chip-index]');
    for (const chip of Array.from(chips)) {
      const r = chip.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return Number(chip.dataset.chipIndex);
    }
    return null;
  }, []);

  const onMove = useCallback(
    (event: globalThis.PointerEvent): void => {
      const s = session.current;
      if (!s) return;
      if (!s.active) {
        if (Math.hypot(event.clientX - s.startX, event.clientY - s.startY) > MOVE_START_PX) {
          if (holdTimer.current) {
            clearTimeout(holdTimer.current);
            holdTimer.current = null;
          }
          finishRef.current(false);
        }
        return;
      }
      event.preventDefault();
      const over = hitTest(event.clientX, event.clientY);
      if (over !== null) {
        overRef.current = over;
        setOverIndex(over);
      }
    },
    [hitTest],
  );
  const onUp = useCallback((): void => finishRef.current(true), []);

  const finish = useCallback(
    (commit: boolean): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
      const s = session.current;
      const target = overRef.current;
      if (commit && s && s.active && target !== null && target !== s.index) {
        const next = [...emojisRef.current];
        const [moved] = next.splice(s.index, 1);
        next.splice(target, 0, moved as string);
        onChangeRef.current(next);
      }
      session.current = null;
      overRef.current = null;
      setDragIndex(null);
      setOverIndex(null);
    },
    [onMove, onUp],
  );
  finishRef.current = finish;

  const onChipPointerDown = (event: ReactPointerEvent<HTMLDivElement>, index: number): void => {
    if (event.button != null && event.button !== 0) return;
    session.current = { index, startX: event.clientX, startY: event.clientY, active: false };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    holdTimer.current = setTimeout(() => {
      const s = session.current;
      if (!s) return;
      s.active = true;
      overRef.current = s.index;
      setDragIndex(s.index);
      setOverIndex(s.index);
    }, HOLD_MS);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (holdTimer.current) clearTimeout(holdTimer.current);
    };
  }, [onMove, onUp]);

  return (
    <div className="seq-editor">
      <div className="seq-editor__head">
        <span className="muted" style={{ fontSize: 12 }}>
          {emojis.length} / {max}
        </span>
        {emojis.length > 1 && (
          <span className="muted" style={{ fontSize: 12 }}>
            {t('presets.reorderHint')}
          </span>
        )}
      </div>

      <div ref={listRef} className="seq-chips">
        {emojis.map((emoji, index) => (
          <div
            key={`${emoji}-${index}`}
            data-chip-index={index}
            className={`seq-chip${dragIndex === index ? ' seq-chip--dragging' : ''}${
              overIndex === index && dragIndex !== null && dragIndex !== index ? ' seq-chip--over' : ''
            }`}
            onPointerDown={(event) => onChipPointerDown(event, index)}
          >
            <span className="seq-chip__glyph">{emoji}</span>
            <button
              type="button"
              className="seq-chip__remove"
              aria-label={t('common.remove')}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => removeAt(index)}
            >
              <Icon name="close" size={12} />
            </button>
          </div>
        ))}

        {!full && (
          <button
            type="button"
            className={`seq-chip seq-chip--add${adding ? ' seq-chip--add-active' : ''}`}
            onClick={() => setAdding((v) => !v)}
            aria-label={t('presets.add')}
          >
            <Icon name={adding ? 'close' : 'plus'} size={18} />
          </button>
        )}
      </div>

      {adding && !full && (
        <div className="seq-editor__board">
          <EmojiBoard onSelect={append} />
        </div>
      )}
    </div>
  );
}
